import { useTranslation } from 'react-i18next';
import { ZoomIn, ZoomOut, Maximize2, Minimize2, ScanSearch, Download } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { FloatingLabelInput } from '@/shared/components/custom/form-field/FloatingLabelInput';
import { cn } from '@/core/lib/utils';
import type { OrgChartView } from './OrgChart';

interface OrgChartToolbarProps {
  activeView: OrgChartView;
  onViewChange: (view: OrgChartView) => void;
  search: string;
  onSearchChange: (value: string) => void;
  isFullscreen: boolean;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitView: () => void;
  onToggleFullscreen: () => void;
  onExport: () => void;
  isExporting: boolean;
}

export function OrgChartToolbar({
  activeView,
  onViewChange,
  search,
  onSearchChange,
  isFullscreen,
  onZoomIn,
  onZoomOut,
  onFitView,
  onToggleFullscreen,
  onExport,
  isExporting,
}: OrgChartToolbarProps) {
  const { t } = useTranslation('organizationUnits');

  return (
    <div
      className="flex flex-col gap-2 border-b bg-background px-4 py-2 md:flex-row md:items-center md:justify-between md:gap-4"
      data-testid="org-chart-toolbar"
    >
      <div className="flex items-center gap-3">
        <h1 className="text-base font-semibold text-foreground">{t('page.title')}</h1>

        <div className="flex rounded-md border bg-muted/40 p-0.5">
          <button
            type="button"
            onClick={() => onViewChange('organization')}
            className={cn(
              'rounded px-3 py-1 text-xs font-medium transition-colors',
              activeView === 'organization'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            data-testid="org-chart-tab-organization"
          >
            {t('orgChart.tabs.organization')}
          </button>

          <button
            type="button"
            onClick={() => onViewChange('projects')}
            className={cn(
              'rounded px-3 py-1 text-xs font-medium transition-colors',
              activeView === 'projects'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            data-testid="org-chart-tab-projects"
          >
            {t('orgChart.tabs.projects')}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="w-48">
          <FloatingLabelInput
            id="org-chart-search"
            label={t('orgChart.search.placeholder')}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            hasValue={!!search}
            data-testid="org-chart-search-input"
          />
        </div>

        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onZoomIn}
                className="h-8 w-8"
                aria-label={t('orgChart.controls.zoomIn')}
                data-testid="org-chart-zoom-in"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('orgChart.controls.zoomIn')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onZoomOut}
                className="h-8 w-8"
                aria-label={t('orgChart.controls.zoomOut')}
                data-testid="org-chart-zoom-out"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('orgChart.controls.zoomOut')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onFitView}
                className="h-8 w-8"
                aria-label={t('orgChart.controls.fitView')}
                data-testid="org-chart-fit-view"
              >
                <ScanSearch className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('orgChart.controls.fitView')}</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleFullscreen}
                className="h-8 w-8"
                aria-label={t('orgChart.controls.fullscreen')}
                data-testid="org-chart-fullscreen"
              >
                {isFullscreen ? (
                  <Minimize2 className="h-4 w-4" />
                ) : (
                  <Maximize2 className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('orgChart.controls.fullscreen')}</TooltipContent>
          </Tooltip>
        </div>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={onExport}
              disabled={isExporting}
              data-testid="org-chart-export-button"
              className="gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              {isExporting ? t('orgChart.export.exporting') : t('orgChart.export.label')}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('orgChart.export.label')}</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
